import axios, { AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import { message } from 'ant-design-vue'
import router from '@/router'
import { useAuthStore } from '@/stores/auth'
import type { ApiResponse } from '@/types/api'
import { AuthExpiredError, BusinessError, HttpError } from '@/utils/errors'

let isHandlingExpiry = false

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 15_000,
})

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore().user?.token
  if (token) config.headers.set('token', token)
  return config
})

export async function expireSession(error: AuthExpiredError): Promise<never> {
  // 多个并发请求可能同时发现 token 失效，只允许首个请求提示并跳转。
  if (!isHandlingExpiry) {
    isHandlingExpiry = true
    useAuthStore().clear()
    message.error('登录状态已失效，请重新登录')
    await router.replace({
      path: '/login',
      query: { redirect: router.currentRoute.value.fullPath },
    })
    window.setTimeout(() => {
      isHandlingExpiry = false
    }, 0)
  }
  throw error
}

apiClient.interceptors.response.use(
  async (response: AxiosResponse<ApiResponse<unknown>>) => {
    // 文件响应可能包含二进制内容，业务错误由 downloadBlob 在读取 Blob 后解析。
    if (response.config.responseType === 'blob' || response.config.responseType === 'arraybuffer')
      return response
    const body = response.data
    if (body?.code === 1201)
      return expireSession(new AuthExpiredError(1201, body.msg || '登录已失效'))
    if (body?.code !== 200) throw new BusinessError(body?.code ?? -1, body?.msg || '业务请求失败')
    return response
  },
  async (error: AxiosError) => {
    if (error.code === 'ERR_CANCELED') throw error
    if (error.response?.status === 401)
      return expireSession(new AuthExpiredError(401, '登录已失效', { cause: error }))
    throw new HttpError(error.response?.status ?? 0, error.message || '网络请求失败', {
      cause: error,
    })
  },
)

export async function requestData<T>(promise: Promise<AxiosResponse<ApiResponse<T>>>): Promise<T> {
  const response = await promise
  return response.data.data
}
